import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "./api";

const SiteCtx = createContext(null);
const AuthCtx = createContext(null);
const CartCtx = createContext(null);

export const useSite = () => useContext(SiteCtx);
export const useAuth = () => useContext(AuthCtx);
export const useCart = () => useContext(CartCtx);

const readLS = (k, d) => {
  try {
    return JSON.parse(localStorage.getItem(k)) ?? d;
  } catch {
    return d;
  }
};

function SiteProvider({ children }) {
  const [site, setSite] = useState(null);
  const reload = useCallback(() => api.get("/site").then((r) => setSite(r.data)).catch(() => setSite({ settings: {}, categories: [], slides: [] })), []);
  useEffect(() => {
    reload();
  }, [reload]);
  return <SiteCtx.Provider value={{ site, reload }}>{children}</SiteCtx.Provider>;
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  useEffect(() => {
    if (window.location.hash?.includes("session_id=") || !localStorage.getItem("hm_token")) {
      setUser(null);
      return;
    }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {
      localStorage.removeItem("hm_token");
      setUser(null);
    });
  }, []);
  const signIn = (data) => {
    localStorage.setItem("hm_token", data.token);
    setUser(data.user);
  };
  const logout = () => {
    localStorage.removeItem("hm_token");
    setUser(null);
  };
  return <AuthCtx.Provider value={{ user, setUser, signIn, logout }}>{children}</AuthCtx.Provider>;
}

function CartProvider({ children }) {
  const [items, setItems] = useState(() => readLS("hm_cart", []));
  const [favs, setFavs] = useState(() => readLS("hm_favs", []));
  const [open, setOpen] = useState(false);
  useEffect(() => {
    localStorage.setItem("hm_cart", JSON.stringify(items));
  }, [items]);
  useEffect(() => {
    localStorage.setItem("hm_favs", JSON.stringify(favs));
  }, [favs]);

  const add = (p, qty = 1) => {
    setItems((cur) => {
      const ex = cur.find((i) => i.product_id === p.id);
      if (ex) return cur.map((i) => (i.product_id === p.id ? { ...i, qty: Math.min(99, i.qty + qty) } : i));
      return [...cur, { product_id: p.id, name: p.name, slug: p.slug, price: p.sale_price && p.sale_price < p.price ? p.sale_price : p.price, image: p.images?.[0], qty }];
    });
    toast.success(`„${p.name}“ přidáno do košíku`);
    setOpen(true);
  };
  const setQty = (id, qty) => setItems((cur) => (qty < 1 ? cur.filter((i) => i.product_id !== id) : cur.map((i) => (i.product_id === id ? { ...i, qty: Math.min(99, qty) } : i))));
  const remove = (id) => setItems((cur) => cur.filter((i) => i.product_id !== id));
  const clear = () => setItems([]);
  const toggleFav = (p) => setFavs((cur) => (cur.find((f) => f.id === p.id) ? cur.filter((f) => f.id !== p.id) : [...cur, p]));
  const count = items.reduce((s, i) => s + i.qty, 0);
  const total = items.reduce((s, i) => s + i.qty * i.price, 0);
  const value = useMemo(() => ({ items, add, setQty, remove, clear, count, total, open, setOpen, favs, toggleFav }), [items, open, favs]); // eslint-disable-line
  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export function AppProvider({ children }) {
  return (
    <SiteProvider>
      <AuthProvider>
        <CartProvider>{children}</CartProvider>
      </AuthProvider>
    </SiteProvider>
  );
}
